import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { nav } from '../../content/agencyCopy';
import FrontechLogo from '../FrontechLogo';

export default function AgencyNav() {
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  // Show auth CTAs on Front SaaS pages
  const showAuthCtas = pathname === '/front' || pathname === '/front-os';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: scrolled
          ? 'color-mix(in oklab, var(--fa-paper) 88%, transparent)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--fa-rule)' : '1px solid transparent',
        transition: 'background 0.2s ease, border-color 0.2s ease',
      }}
      aria-label="Primary"
    >
      <div className="max-w-[1120px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="font-display font-bold text-[17px] tracking-tight flex items-center gap-2"
          style={{ color: 'var(--fa-ink)', letterSpacing: '0.02em' }}
        >
          <FrontechLogo size={22} variant="icon" color="var(--fa-signal)" />
          {nav.brand}
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          <div
            className="relative"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              className="flex items-center gap-1 text-[14px] font-medium py-2"
              style={{ color: 'var(--fa-ink)' }}
              onClick={() => setProductsOpen((v) => !v)}
              aria-expanded={productsOpen}
              aria-haspopup="menu"
            >
              Products
              <ChevronDown size={14} style={{ color: 'var(--fa-muted)' }} />
            </button>
            {productsOpen && (
              <div
                role="menu"
                className="absolute left-0 top-full pt-2 min-w-[280px]"
              >
                <div
                  className="rounded overflow-hidden"
                  style={{
                    background: 'var(--fa-paper)',
                    border: '1px solid var(--fa-rule)',
                    boxShadow: '0 8px 24px rgba(20, 24, 26, 0.06)',
                  }}
                >
                  {nav.products.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      role="menuitem"
                      className="block px-4 py-3 hover:bg-[var(--fa-surface)]"
                      style={{ borderBottom: '1px solid var(--fa-rule)' }}
                    >
                      <div className="text-[14px] font-medium" style={{ color: 'var(--fa-ink)' }}>
                        {p.label}
                      </div>
                      <div className="text-[12px] mt-0.5" style={{ color: 'var(--fa-muted)' }}>
                        {p.hint}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {nav.links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-[14px] font-medium"
              style={{ color: 'var(--fa-ink)' }}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Theme toggle — bordered pill, always visible */}
          <button
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="fa-theme-toggle inline-flex items-center gap-2 rounded-lg transition-colors"
            style={{
              background: 'var(--fa-surface)',
              border: '1px solid var(--fa-rule)',
              color: 'var(--fa-ink)',
              padding: '7px 10px',
            }}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {showAuthCtas && (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/login"
                className="text-[13.5px] font-medium px-3.5 py-2 rounded-lg transition-colors"
                style={{
                  color: 'var(--fa-ink)',
                  background: 'var(--fa-surface)',
                  border: '1px solid var(--fa-rule)',
                }}
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="fa-signal-cta fa-signal-cta--sm"
              >
                <span className="fa-signal-cta__label">Get started</span>
              </Link>
            </div>
          )}

          <button
            className="md:hidden p-2 rounded"
            style={{ color: 'var(--fa-ink)' }}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d={menuOpen ? 'M6 6l12 12M6 18L18 6' : 'M4 7h16M4 12h16M4 17h16'}
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden border-t"
          style={{ borderColor: 'var(--fa-rule)', background: 'var(--fa-paper)' }}
        >
          <div className="max-w-[1120px] mx-auto px-6 py-3 flex flex-col gap-1">
            {nav.products.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                onClick={() => setMenuOpen(false)}
                className="py-2 text-[15px] font-medium"
                style={{ color: 'var(--fa-ink)' }}
              >
                {p.label}{' '}
                <span className="text-[12px]" style={{ color: 'var(--fa-muted)' }}>
                  · {p.hint}
                </span>
              </Link>
            ))}
            {nav.links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="py-2 text-[15px] font-medium"
                style={{ color: 'var(--fa-ink)' }}
              >
                {l.label}
              </Link>
            ))}
            {showAuthCtas && (
              <div className="mt-2 pt-3 flex flex-col gap-2" style={{ borderTop: '1px solid var(--fa-rule)' }}>
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="py-2 text-[15px] font-medium"
                  style={{ color: 'var(--fa-ink)' }}
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="py-2 text-[15px] font-medium"
                  style={{ color: 'var(--fa-signal)' }}
                >
                  Get started →
                </Link>
              </div>
            )}
            <button
              onClick={toggle}
              className="flex items-center gap-3 py-3 mt-2 text-[15px] font-medium border-t"
              style={{ color: 'var(--fa-ink)', borderColor: 'var(--fa-rule)' }}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
